import {
  EVENT_LABELS,
  EventType,
  sumDecimals,
  type EntryFull,
  type EntryRevision,
} from '@siteday/shared';

import { parseSnapshot, type Snapshot } from '@/lib/diff';
import { formatHours, formatMinutes, formatQuantity } from '@/lib/format';

import type { EntryDocument } from './editSchema';

export type SectionKey = 'crew' | 'equipment' | 'quantities' | 'delays' | 'notes' | 'photos';
export const SECTIONS: Array<{ key: SectionKey; label: string }> = [
  { key: 'crew', label: 'Crew' },
  { key: 'equipment', label: 'Equipment' },
  { key: 'quantities', label: 'Quantities' },
  { key: 'delays', label: 'Delays' },
  { key: 'notes', label: 'Notes' },
  { key: 'photos', label: 'Photos' },
];
export type Side = 'office' | 'field';
export type Picks = Record<SectionKey, Side>;

const live = <T extends { deletedAt?: string | null | undefined }>(xs: T[] | undefined) =>
  (xs ?? []).filter((x) => !x.deletedAt);

/** The two versions in a needs_review entry: latest office revision and latest conflict (field) revision. */
export function conflictSides(revisions: EntryRevision[]): {
  office: EntryRevision | null;
  field: EntryRevision | null;
} {
  const sorted = [...revisions].sort((a, b) => b.version - a.version);
  return {
    office: sorted.find((r) => r.source === 'office') ?? null,
    field:
      sorted.find((r) => r.source === 'conflict') ??
      sorted.find((r) => r.source === 'foreman') ??
      null,
  };
}

/** Plain-text lines per section so both columns can be compared and highlighted. */
export function summarizeSections(s: Snapshot | null): Record<SectionKey, string[]> {
  if (!s)
    return {
      crew: ['—'],
      equipment: ['—'],
      quantities: ['—'],
      delays: ['—'],
      notes: ['—'],
      photos: ['—'],
    };
  const crew = live(s.crewLines);
  const eq = live(s.equipmentLines);
  const qty = live(s.quantityLines).filter((q) => Number(q.quantity) !== 0);
  const ev = live(s.events);
  return {
    crew: [
      `${crew.length} people · ${formatHours(sumDecimals(crew.map((c) => c.hours)))} h`,
      ...crew.map((c) => `${c.userName ?? 'Crew member'} ${formatHours(c.hours)}`),
    ],
    equipment: eq.length
      ? eq.map((e) => `${e.equipmentName ?? 'Machine'} ${formatHours(e.hours)} h`)
      : ['none'],
    quantities: qty.length
      ? qty.map((q) => `${q.code ?? ''} ${formatQuantity(q.quantity)}`)
      : ['none'],
    delays: ev.length
      ? ev.map(
          (e) =>
            `${(EVENT_LABELS as Record<string, string>)[e.type] ?? e.type} · ${formatMinutes(e.durationMinutes)}${e.description ? ` · "${e.description}"` : ''}`,
        )
      : ['none'],
    notes: [s.notes?.trim() ? s.notes : '—'],
    photos: [String(live(s.photos).length)],
  };
}

export function sectionsDiffer(
  a: Snapshot | null,
  b: Snapshot | null,
): Record<SectionKey, boolean> {
  const sa = summarizeSections(a);
  const sb = summarizeSections(b);
  const out = {} as Record<SectionKey, boolean>;
  for (const { key } of SECTIONS) out[key] = sa[key].join('\n') !== sb[key].join('\n');
  return out;
}

/** Default picks: the field version where it differs (the phone is the source of truth for the day), office elsewhere. */
export function defaultPicks(differs: Record<SectionKey, boolean>): Picks {
  const p = {} as Picks;
  for (const { key } of SECTIONS) p[key] = differs[key] ? 'field' : 'office';
  return p;
}

/** Build the merged document from per-section choices. */
export function buildMergedDocument(
  entry: EntryFull,
  office: Snapshot | null,
  field: Snapshot | null,
  picks: Picks,
): EntryDocument {
  const from = (k: SectionKey) => (picks[k] === 'field' ? field : office) ?? office ?? field;
  const crew = live(from('crew')?.crewLines).map((l) => ({
    id: l.id,
    userId: l.userId,
    hours: l.hours,
  }));
  const equipment = live(from('equipment')?.equipmentLines).map((l) => ({
    id: l.id,
    equipmentId: l.equipmentId,
    hours: l.hours,
  }));
  const quantities = live(from('quantities')?.quantityLines).map((l) => ({
    id: l.id,
    jobCostCodeId: l.jobCostCodeId,
    quantity: l.quantity,
  }));
  const events = live(from('delays')?.events).map((e) => ({
    id: e.id,
    type: EventType.safeParse(e.type).success ? (e.type as EventType) : ('other' as const),
    durationMinutes: e.durationMinutes,
    description: e.description,
  }));
  const notesSide = from('notes');
  const photosSide = from('photos');
  const keptPhotoIds = new Set(live(photosSide?.photos).map((p) => p.id));
  return {
    id: entry.id,
    jobId: entry.jobId,
    foremanId: entry.foremanId,
    entryDate: entry.entryDate,
    status: entry.status,
    weatherCondition: entry.weatherCondition,
    temperatureF: entry.temperatureF,
    notes: notesSide?.notes ?? entry.notes,
    finishedAt: entry.finishedAt,
    crewLines: crew,
    equipmentLines: equipment,
    quantityLines: quantities,
    events,
    photos: entry.photos.map((p) => ({
      id: p.id,
      takenAt: p.takenAt,
      caption: p.caption,
      width: p.width,
      height: p.height,
      bytes: p.bytes,
      ...(photosSide && !keptPhotoIds.has(p.id) ? { deletedAt: new Date().toISOString() } : {}),
    })),
  };
}

export function snapshotOfRevision(r: EntryRevision | null): Snapshot | null {
  return r ? parseSnapshot(r.snapshot) : null;
}
